import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  User as FUser,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  reload,
  signInAnonymously,
  signInWithCustomToken,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'

import { auth, functions, onAuthStateChanged } from '@/shared/firebase/firebase.ts'

import { TrackingService } from './lib/tracking'

type User = FUser & { isStaff: boolean }

interface AuthContextType {
  user?: User
  isLoggedIn: boolean
  isLoadingUser: boolean
  isEmailVerified: boolean
  anonymousSignIn: () => Promise<User>
  signInWithSecret: (secret: string) => Promise<User>
  signInWithEmail: (email: string, password: string) => Promise<User>
  signUpWithEmail: (email: string, password: string) => Promise<User>
  signOut: () => Promise<void>
  sendPasswordReset: (email: string) => Promise<{ data: { success: boolean; userExists?: boolean; emailSent?: boolean } }>
  sendEmailVerification: (email?: string) => Promise<void>
  reloadUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within a UserProvider')
  }
  return context
}

export const useUser = () => {
  const context = useAuth()
  if (context.user === undefined) {
    throw new Error('useUser must be used within an authenticated UserProvider')
  }
  return context.user
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language?.startsWith('es') ? 'es' : i18n.language?.startsWith('en') ? 'en' : 'es'

  const isLoggedIn = user !== undefined
  const isEmailVerified = user?.emailVerified ?? false

  useEffect(() => {
    let isMounted = true

    const unsubscribe = onAuthStateChanged(async newUser => {
      if (!isMounted) return

      try {
        if (newUser) {
          const token = await newUser.getIdTokenResult()
          
          if (!isMounted) return

          // @ts-expect-error adding custom property to user.
          //  Using it this way to avoid losing the methods of the user object
          newUser.isStaff = !!token.claims.isStaff
          
          setUser(newUser as User)
          TrackingService.identify(newUser.uid)
          TrackingService.setUser({
            id: newUser.uid,
            avatar: newUser.photoURL,
            email: newUser.email,
            name: newUser.displayName,
            phone: newUser.phoneNumber,
            // @ts-expect-error custom property of user.
            type: newUser.isStaff ? 'staff' : 'end-customer',
          })
          console.log('newUser', newUser)
        } else {
          setUser(undefined)
        }
      } catch (error) {
        console.error('Error in auth state change:', error)
        if (isMounted) {
          setUser(undefined)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const anonymousSignIn = async () => {
    setIsLoading(true)
    return await signInAnonymously(auth)
      .then(
        (credentials): User => ({
          ...credentials.user,
          isStaff: false,
        }),
      )
      .finally(() => setIsLoading(false))
  }

  const signInWithSecret = async (secret: string) => {
    setIsLoading(true)
    try {
      const authenticateWithSecret = httpsCallable(functions, 'authenticateWithSecret')
      const result = await authenticateWithSecret({ secret })
      const { token } = result.data as { token: string }
      const credentials = await signInWithCustomToken(auth, token)
      return {
        ...credentials.user,
        isStaff: false,
      }
    } finally {
      setIsLoading(false)
    }
  }

  const signInWithEmail = async (email: string, password: string): Promise<User> => {
    // Don't set isLoading here - let the component handle its own loading state
    const credentials = await signInWithEmailAndPassword(auth, email, password)
    const token = await credentials.user.getIdTokenResult()
    return {
      ...credentials.user,
      isStaff: !!token.claims.isStaff,
    }
  }

  const signUpWithEmail = async (email: string, password: string): Promise<User> => {
    // Don't set isLoading here - let the component handle its own loading state
    const credentials = await createUserWithEmailAndPassword(auth, email, password)
    // Send verification email using Firebase Auth
    try {
      await sendEmailVerification(email)
    } catch (error) {
      console.error('Error sending verification email:', error)
      // Don't throw - user was created successfully, email sending is secondary
    }
    return {
      ...credentials.user,
      isStaff: false,
    }
  }

  const signOutUser = async (): Promise<void> => {
    // Don't set isLoading here - the auth state change will be handled by onAuthStateChanged
    await firebaseSignOut(auth)
  }

  const sendPasswordReset = async (email: string): Promise<{ data: { success: boolean; userExists?: boolean; emailSent?: boolean } }> => {
    const sendPasswordResetEmail = httpsCallable<{ email: string; language: string }, { success: boolean; userExists?: boolean; emailSent?: boolean }>(functions, 'sendPasswordReset')
    const result = await sendPasswordResetEmail({ email, language: currentLanguage })
    return { data: result.data }
  }

  const sendEmailVerification = async (email?: string): Promise<void> => {
    const targetEmail = email ?? user?.email ?? auth.currentUser?.email
    if (!targetEmail) {
      throw new Error('Email is required to send verification')
    }
    const sendEmailVerificationEmail = httpsCallable(functions, 'sendEmailVerification')
    await sendEmailVerificationEmail({ email: targetEmail, language: currentLanguage })
  }

  const reloadUser = async (): Promise<void> => {
    if (user) {
      await reload(user)
      // Trigger a state update by re-fetching the user
      const currentUser = auth.currentUser
      if (currentUser) {
        const token = await currentUser.getIdTokenResult()
        setUser({
          ...currentUser,
          isStaff: !!token.claims.isStaff,
        } as User)
      }
    }
  }

  const value: AuthContextType = {
    anonymousSignIn,
    signInWithSecret,
    signInWithEmail,
    signUpWithEmail,
    signOut: signOutUser,
    sendPasswordReset,
    sendEmailVerification,
    reloadUser,
    user,
    isLoggedIn,
    isLoadingUser: isLoading,
    isEmailVerified,
  }

  return <AuthContext.Provider value={value}> {children} </AuthContext.Provider>
}
